import { useState, useEffect } from 'react';
import { UsageRecord } from '@/lib/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { format } from 'date-fns';

interface UsageRecordWithProduct extends UsageRecord {
  productName: string;
  productCategory: string;
}

interface RegistrationGroup {
  id: string;
  type: 'individual' | 'group';
  date: Date;
  records: UsageRecordWithProduct[];
  totalProducts: number;
  totalQuantity: number;
}

// Helper function to group records by session
const groupRecordsBySession = (records: UsageRecordWithProduct[]): RegistrationGroup[] => {
  const groups: Record<string, UsageRecordWithProduct[]> = {};
  const individual: UsageRecordWithProduct[] = [];

  // Separate group sessions from individual records
  records.forEach(record => {
    if (record.groupSessionId) {
      if (!groups[record.groupSessionId]) {
        groups[record.groupSessionId] = [];
      }
      groups[record.groupSessionId].push(record);
    } else {
      individual.push(record);
    }
  });

  const result: RegistrationGroup[] = [];

  // Add group sessions
  Object.entries(groups).forEach(([sessionId, sessionRecords]) => {
    result.push({
      id: sessionId,
      type: 'group',
      date: sessionRecords[0].date,
      records: sessionRecords,
      totalProducts: sessionRecords.length,
      totalQuantity: sessionRecords.reduce((sum, r) => sum + r.quantity, 0)
    });
  });

  // Add individual records
  individual.forEach(record => {
    result.push({
      id: record.id,
      type: 'individual',
      date: record.date,
      records: [record],
      totalProducts: 1,
      totalQuantity: record.quantity
    });
  });

  // Sort by date (newest first)
  return result.sort((a, b) => b.date.getTime() - a.date.getTime());
};

export const useDailyRegistrations = (selectedDate: Date) => {
  const [registrations, setRegistrations] = useState<RegistrationGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const fetchRegistrations = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const dateString = format(selectedDate, 'yyyy-MM-dd');
        const startDate = `${dateString}T00:00:00.000Z`;
        const endDate = `${dateString}T23:59:59.999Z`;

        const { data, error } = await supabase
          .from('usage_records')
          .select(`
            *,
            products!inner(
              id,
              name,
              category
            )
          `)
          .eq('user_id', user.id)
          .gte('date', startDate)
          .lte('date', endDate)
          .order('date', { ascending: false });

        if (error) throw error;

        // Transform the data to match our UsageRecord type and group by sessions
        const rawRecords: UsageRecordWithProduct[] = (data || []).map(record => ({
          id: record.id,
          productId: record.product_id,
          quantity: record.quantity,
          date: new Date(record.date),
          groupSessionId: record.group_session_id || undefined,
          outputVolume: record.output_volume || undefined,
          consistency: (record.consistency as 'liquid' | 'semi-solid' | 'solid' | 'other') || undefined,
          color: record.color || undefined,
          notes: record.notes || undefined,
          // Add product info for display
          productName: record.products?.name || 'Unknown Product',
          productCategory: record.products?.category || 'other'
        }));

        // Group records by session ID for display
        const groupedRecords = groupRecordsBySession(rawRecords);
        setRegistrations(groupedRecords);
      } catch (err: any) {
        console.error('Error fetching daily registrations:', err);
        setError(err.message || 'Failed to load registrations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRegistrations();
  }, [selectedDate, user]);

  return {
    registrations,
    isLoading,
    error,
    count: registrations.length
  };
};